/**
 * Evolution Charts - Display patient evolution graphs
 * Individual charts, 2 per row, limited to last 5 consultations
 */
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp } from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { NORMAL_VALUES } from './utils';

// Individual chart component
const MiniChart = ({ 
  data, 
  dataKey, 
  title, 
  unit, 
  color, 
  normalMin, 
  normalMax,
  yDomain 
}) => {
  // Get last 5 data points that have the value
  const chartData = useMemo(() => {
    const filtered = data.filter(d => d[dataKey] !== undefined && d[dataKey] !== null);
    return filtered.slice(-5);
  }, [data, dataKey]);

  if (chartData.length === 0) return null;

  // Get latest value for display
  const latestValue = chartData[chartData.length - 1]?.[dataKey];
  const isNormal = normalMin !== undefined && normalMax !== undefined
    ? latestValue >= normalMin && latestValue <= normalMax
    : true;

  // Calculate Y domain based on data
  const values = chartData.map(d => d[dataKey]).filter(v => v !== null);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const padding = (maxVal - minVal) * 0.2 || 10;
  const calculatedDomain = yDomain || [
    Math.floor(Math.min(minVal - padding, normalMin || minVal)),
    Math.ceil(Math.max(maxVal + padding, normalMax || maxVal))
  ];

  return (
    <div className="bg-white border rounded-lg p-3" data-testid={`chart-${dataKey}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-slate-700">{title}</h4>
        <div className={`text-lg font-bold ${isNormal ? 'text-emerald-600' : 'text-red-600'}`}>
          {latestValue} <span className="text-xs font-normal text-slate-500">{unit}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => {
              const parts = value.split('-');
              return `${parts[2]}/${parts[1]}`;
            }}
          />
          <YAxis 
            domain={calculatedDomain}
            tick={{ fontSize: 10 }}
            width={35}
          />
          <Tooltip 
            contentStyle={{ fontSize: 12 }}
            formatter={(value) => [`${value} ${unit}`, title]}
            labelFormatter={(label) => `Data: ${label}`}
          />
          {/* Reference lines for normal range */}
          {normalMin !== undefined && (
            <ReferenceLine 
              y={normalMin} 
              stroke="#10b981" 
              strokeDasharray="5 5" 
              strokeWidth={1}
            />
          )}
          {normalMax !== undefined && (
            <ReferenceLine 
              y={normalMax} 
              stroke="#10b981" 
              strokeDasharray="5 5" 
              strokeWidth={1}
            />
          )}
          <Line 
            type="monotone" 
            dataKey={dataKey} 
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4, fill: color }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {normalMin !== undefined && normalMax !== undefined && (
        <p className="text-xs text-slate-400 text-center mt-1">
          Normal: {normalMin} - {normalMax} {unit}
        </p>
      )}
    </div>
  );
};

export const EvolutionCharts = ({ evolution }) => {
  if (!evolution || evolution.length === 0) {
    return (
      <Card data-testid="evolution-charts">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Evoluție Pacient
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-8">
            Nu există date suficiente pentru grafice. 
            Adăugați consultații pentru a vedea evoluția pacientului.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Check which data points exist
  const hasGlycemia = evolution.some(e => e.glycemia);
  const hasHbA1c = evolution.some(e => e.hba1c);
  const hasWeight = evolution.some(e => e.weight);
  const hasBMI = evolution.some(e => e.bmi);
  const hasHDL = evolution.some(e => e.hdl);
  const hasLDL = evolution.some(e => e.ldl);
  const hasTriglycerides = evolution.some(e => e.triglycerides);
  const hasSystolic = evolution.some(e => e.systolic);
  const hasDiastolic = evolution.some(e => e.diastolic);

  // Build list of available charts
  const charts = [];

  if (hasGlycemia) {
    charts.push({
      dataKey: 'glycemia',
      title: 'Glicemie',
      unit: 'mg/dL',
      color: '#ef4444',
      normalMin: NORMAL_VALUES.glycemia.min,
      normalMax: NORMAL_VALUES.glycemia.max
    });
  }

  if (hasHbA1c) {
    charts.push({
      dataKey: 'hba1c',
      title: 'HbA1c',
      unit: '%',
      color: '#f59e0b',
      normalMin: NORMAL_VALUES.hba1c.min,
      normalMax: NORMAL_VALUES.hba1c.max
    });
  }

  if (hasWeight) {
    charts.push({
      dataKey: 'weight',
      title: 'Greutate',
      unit: 'kg',
      color: '#3b82f6'
    });
  }

  if (hasBMI) {
    charts.push({
      dataKey: 'bmi',
      title: 'IMC',
      unit: 'kg/m²',
      color: '#8b5cf6',
      normalMin: NORMAL_VALUES.bmi.min,
      normalMax: NORMAL_VALUES.bmi.max
    });
  }

  if (hasHDL) {
    charts.push({
      dataKey: 'hdl',
      title: 'HDL Colesterol',
      unit: 'mg/dL',
      color: '#10b981',
      normalMin: NORMAL_VALUES.hdl.min,
      normalMax: 100 // Display max for reference
    });
  }

  if (hasLDL) {
    charts.push({
      dataKey: 'ldl',
      title: 'LDL Colesterol',
      unit: 'mg/dL',
      color: '#ef4444',
      normalMin: 0,
      normalMax: NORMAL_VALUES.ldl.max
    });
  }

  if (hasTriglycerides) {
    charts.push({
      dataKey: 'triglycerides',
      title: 'Trigliceride',
      unit: 'mg/dL',
      color: '#f97316',
      normalMin: 0,
      normalMax: NORMAL_VALUES.triglycerides.max
    });
  }

  if (hasSystolic) {
    charts.push({
      dataKey: 'systolic',
      title: 'TA Sistolică',
      unit: 'mmHg',
      color: '#dc2626',
      normalMin: 90,
      normalMax: 120
    });
  }

  if (hasDiastolic) {
    charts.push({
      dataKey: 'diastolic',
      title: 'TA Diastolică',
      unit: 'mmHg',
      color: '#2563eb',
      normalMin: 60,
      normalMax: 80
    });
  }

  return (
    <Card data-testid="evolution-charts">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Evoluție Pacient
          <span className="text-sm font-normal text-slate-500 ml-2">
            (ultimele 5 consultații)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {charts.map((chart) => (
            <MiniChart
              key={chart.dataKey}
              data={evolution}
              dataKey={chart.dataKey}
              title={chart.title}
              unit={chart.unit}
              color={chart.color}
              normalMin={chart.normalMin}
              normalMax={chart.normalMax}
              yDomain={chart.yDomain}
            />
          ))}
        </div>
        {charts.length === 0 && (
          <p className="text-slate-500 text-center py-8">
            Nu există parametri înregistrați în consultații.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
